import {
	Box,
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	TextField,
	Typography,
	Chip,
	Checkbox,
	FormControlLabel,
} from "@mui/material";
import { grey } from "@mui/material/colors";
import { GlobalState } from "context/GlobalContext";
import { AuthState } from "context/AuthContext";
import { useState, useMemo, useEffect } from "react";
import axios from "axios";
import { parseErrorMessage } from "utils/ErrorFunctions/parseErrorMessage";
import { departments } from "data/authData";

const RemarkModal = ({ modal, setModal, styles, onSubmitSuccess }) => {
	const { user } = AuthState();
	const { setSnackBar, snackBar, setSnackBarOpen, setLoadingOpen } = GlobalState();
	const [remark, setRemark] = useState("");
	const departmentOptions = useMemo(
		() => Object.values(departments).filter((d) => d.key === departments.DESIGNER.key || d.key === departments.SOURCING.key),
		[],
	);
	const [selectedDepartments, setSelectedDepartments] = useState(departmentOptions);

	const styleIds = useMemo(() => styles?.map((s) => s.style_id) || [], [styles]);

	useEffect(() => {
		if (modal) {
			setSelectedDepartments(departmentOptions);
		}
	}, [modal, departmentOptions]);

	const usersToNotify = useMemo(() => {
		const isValidEmail = (value) => {
			const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
			return emailRegex.test(value);
		};
		const userSet = new Set();

		const selectedDeptKeys = selectedDepartments.map((d) => d.key);

		if (selectedDeptKeys.includes(departments.SOURCING.key)) {
			styles?.forEach((style) => {
				if (style.fabric_updated_by && isValidEmail(style.fabric_updated_by)) {
					userSet.add(style.fabric_updated_by);
				}
			});
		}

		if (selectedDeptKeys.includes(departments.DESIGNER.key)) {
			styles?.forEach((style) => {
				if (style.created_by && isValidEmail(style.created_by)) {
					userSet.add(style.created_by);
				}
			});
		}

		if (user?.email) userSet.add(user.email);
		return Array.from(userSet);
	}, [styles, user, selectedDepartments]);

	const handleDepartmentToggle = (dept) => {
		setSelectedDepartments((prev) => {
			const exists = prev.find((d) => d.key === dept.key);
			return exists ? prev.filter((d) => d.key !== dept.key) : [...prev, dept];
		});
	};

	const handleClose = () => {
		setModal(false);
		setRemark("");
		setSelectedDepartments(departmentOptions);
	};

	const handleSubmit = async () => {
		if (!remark.trim()) {
			setSnackBarOpen(true);
			setSnackBar({ ...snackBar, severity: "warning", message: "Please enter a remark" });
			return;
		}

		setLoadingOpen(true);
		try {
			const { data } = await axios.post("/api/designer/add_style_remarks", {
				style_ids: styleIds,
				remark,
				users_to_notify: usersToNotify,
				departments: selectedDepartments.map((d) => d.key),
			});
			setSnackBarOpen(true);
			setSnackBar({ ...snackBar, severity: "success", message: "Remark submitted successfully" });
			setLoadingOpen(false);
			handleClose();
			if (onSubmitSuccess) onSubmitSuccess(data.result);
		} catch (error) {
			setSnackBarOpen(true);
			setSnackBar({ ...snackBar, severity: "error", message: parseErrorMessage(error) });
			setLoadingOpen(false);
		}
	};

	return (
		<Dialog open={modal} onClose={handleClose} maxWidth="sm" fullWidth>
			<DialogTitle>Add Remark</DialogTitle>
			<DialogContent>
				<Box mb={2}>
					<Typography variant="subtitle2" color={grey[700]} mb={1}>
						Style IDs:
					</Typography>
					<Box display="flex" flexWrap="wrap" gap={0.5}>
						{styleIds.map((id) => (
							<Chip key={id} label={id} size="small" />
						))}
					</Box>
				</Box>
				<Box mb={2}>
					<Typography variant="subtitle2" color={grey[700]} mb={1}>
						Select Departments to be Notified:
					</Typography>
					<Box display="flex" gap={0.5}>
						{departmentOptions.map((dept) => (
							<FormControlLabel
								key={dept.key}
								control={
									<Checkbox
										checked={selectedDepartments.some((d) => d.key === dept.key)}
										onChange={() => handleDepartmentToggle(dept)}
										size="small"
									/>
								}
								label={<Typography fontSize={14}>{dept.name}</Typography>}
							/>
						))}
					</Box>
				</Box>
				<Box mb={2}>
					<Typography variant="subtitle2" color={grey[700]} mb={1}>
						Users to be Notified:
					</Typography>
					<Box display="flex" flexWrap="wrap" gap={0.5}>
						{usersToNotify.map((username) => (
							<Chip key={username} label={username} size="small" color="primary" variant="outlined" />
						))}
					</Box>
				</Box>

				<TextField
					label="Remark"
					multiline
					rows={4}
					fullWidth
					value={remark}
					onChange={(e) => setRemark(e.target.value)}
					placeholder="Enter your remark here..."
				/>
			</DialogContent>
			<DialogActions>
				<Button onClick={handleClose}>Cancel</Button>
				<Button onClick={handleSubmit} variant="contained">
					Submit
				</Button>
			</DialogActions>
		</Dialog>
	);
};

export default RemarkModal;
